// The welcome rings, counted over all a search reaches (getRingCounts) rather
// than drawn from the newest of its sessions, so there is no cap. A search
// that keeps only sessions review flagged (a click on the rim, or on the
// centre's met or flagged) reaches only sessions the rim reads anyway: its
// rings are counted from those (countsOfShapes), as a match of them would
// keep only the newest.
//
// useRingCounts gives what lib/sessionFacts.js gives the flow board, in the
// shape the welcome frame reads (`status`, an empty `sessions`), with the
// rings in `rings` (ringsOfCounts). Where review left a session is the
// range's findings', counted where the search reaches it
// (useReviewedInReach); which agent and group each of those sessions had is
// read for them alone (getSessionShapes), a batch at a time. Until that is
// in, `reviewed` is false and the rings are drawn without their rim.
import { useMemo } from "react";
import { useQuery } from "./dataClient.js";
import { useKeyedReads } from "./keyedReads.js";
import { NOT_IN_DIRECTORY } from "./queryLang.js";
import { flaggedOnly, splitReview } from "./reviewClause.js";
import { useReviewedInReach } from "./workspaceSearch.js";

// Where review left a session that was flagged without meeting a standard.
export const FLAGGED_PLACES = ["not_verified", "not_met", "set_aside"];
const IS_FLAGGED = new Set(FLAGGED_PLACES);
const NONE = [];
// The rim's sessions are read this many a batch, under the IN-list cap.
const SHAPES_A_READ = 1000;
const READING = Object.freeze({ status: "loading", sessions: NONE, rings: null, partial: false });
const FAILED = Object.freeze({ status: "error", sessions: NONE, rings: null, partial: false });
const DONE = Object.freeze({ status: "done", sessions: NONE, capped: false, partial: false });

// The rings' tally from their counts (getRingCounts) and, once read, where
// review left the sessions they reach (`review`: { places, shapes }), as
// RingsView draws it: each agent's sessions (`count`) and how many met a
// standard or were flagged, adding up to the centre; and in its `groups`,
// its sessions by group, the same way. A session is of one group, so an
// agent's groups add up to it: those the directory has no one for are the
// rest of its sessions, NOT_IN_DIRECTORY.
export function ringsOfCounts(counts, review = null) {
  const by = new Map();
  for (const a of counts.agents) by.set(a.tag, { tag: a.tag, count: a.sessions, met: 0, flagged: 0, groups: new Map() });
  for (const g of counts.groups) by.get(g.tag)?.groups.set(g.value, arcOf(g.sessions));
  for (const a of by.values()) {
    // Groups that could not be read: each agent's sessions as one arc not
    // read, as the flow board draws them.
    if (counts.groupsFailed) {
      a.groups = new Map([["", arcOf(a.count)]]);
      continue;
    }
    const rest = a.count - [...a.groups.values()].reduce((n, e) => n + e.count, 0);
    if (rest > 0) a.groups.set(NOT_IN_DIRECTORY, arcOf(rest));
  }
  let met = 0;
  let flagged = 0;
  for (const [id, place] of review?.places || []) {
    const m = place === "met" ? 1 : 0;
    const f = IS_FLAGGED.has(place) ? 1 : 0;
    if (!m && !f) continue;
    met += m;
    flagged += f;
    const shape = review.shapes?.[id];
    const a = shape ? by.get(shape.agent) : null;
    if (!a) continue;
    const mark = (e) => {
      if (!e) return;
      e.met += m;
      e.flagged += f;
    };
    mark(a);
    if (counts.groupsFailed) mark(a.groups.get(""));
    else for (const g of shape.groups.length ? new Set(shape.groups) : [NOT_IN_DIRECTORY]) mark(a.groups.get(g));
  }
  // Never more than there is (a shared machine's sessions are in each of its
  // people's groups, as a search for either reaches them).
  for (const a of by.values()) {
    for (const e of [a, ...a.groups.values()]) {
      e.met = Math.min(e.met, e.count);
      e.flagged = Math.min(e.flagged, e.count - e.met);
    }
  }
  const agents = [...by.values()].filter((a) => a.count > 0).sort((x, y) => y.count - x.count || String(x.tag).localeCompare(String(y.tag)));
  return { agents, total: agents.reduce((n, a) => n + a.count, 0), met, flagged };
}

// An arc of `sessions`, before review is read.
export function arcOf(sessions) {
  return { count: sessions || 0, met: 0, flagged: 0 };
}

// The counts getRingCounts gives, of sessions read one by one: `places`
// (session id -> where review left it) and what each was (`shapes`, as
// getSessionShapes reads them). A session is counted on its agent and on
// each of its groups; one with none is left for NOT_IN_DIRECTORY.
export function countsOfShapes(places, shapes) {
  const agents = new Map();
  const groups = new Map();
  for (const id of places.keys()) {
    const shape = shapes?.[id];
    const tag = shape?.agent || "";
    agents.set(tag, (agents.get(tag) || 0) + 1);
    for (const value of new Set(shape?.groups || [])) {
      const key = JSON.stringify([tag, value]);
      groups.set(key, (groups.get(key) || 0) + 1);
    }
  }
  return {
    agents: [...agents].map(([tag, sessions]) => ({ tag, sessions })),
    groups: [...groups].map(([key, sessions]) => {
      const [tag, value] = JSON.parse(key);
      return { tag, value, sessions };
    }),
    groupsFailed: false,
  };
}

// What a click on an agent, on one of its groups (`value`), or on where
// review left a group's sessions (`place`: "met" or "flagged") narrows
// counted rings to, drawn at once from the counts in hand: the agent alone,
// or the agent with only that group, and of those only the sessions at that
// place. Exactly what the narrower search counts.
export function narrowRings(stats, tag, value = null, place = null) {
  const a = stats.agents.find((x) => x.tag === tag);
  const none = { agents: [], total: 0, met: 0, flagged: 0 };
  if (!a) return none;
  if (value == null && !place) return { agents: [a], total: a.count, met: a.met, flagged: a.flagged };
  const at = (e) => (place === "met" ? { count: e.met, met: e.met, flagged: 0 } : place === "flagged" ? { count: e.flagged, met: 0, flagged: e.flagged } : e);
  const kept = value == null ? [...a.groups] : a.groups.has(value) ? [[value, a.groups.get(value)]] : [];
  const groups = new Map(kept.map(([v, e]) => [v, at(e)]).filter(([, e]) => e.count > 0));
  const own = at(value == null ? a : a.groups.get(value) || { count: 0, met: 0, flagged: 0 });
  if (!own.count) return none;
  return { agents: [{ ...a, ...own, groups }], total: own.count, met: own.met, flagged: own.flagged };
}

export function useRingCounts(scope, range, { enabled = true } = {}) {
  const on = enabled && !!scope.ready;
  const drilled = useMemo(() => flaggedOnly(splitReview(scope.q || "").keeps), [scope.q]);
  const read = useQuery("getRingCounts", { scopePath: scope.scopePath, query: scope.steer, range }, { enabled: on && !drilled });
  const reviewed = useReviewedInReach(range, scope.q, { enabled: on });
  const ids = useMemo(() => (reviewed.places
    ? [...reviewed.places].filter(([, place]) => place === "met" || IS_FLAGGED.has(place)).map(([id]) => id).sort()
    : NONE), [reviewed.places]);
  const shapes = useKeyedReads("getSessionShapes", ids, { range }, { enabled: on && ids.length > 0, chunk: SHAPES_A_READ, keep: true });
  return useMemo(() => {
    const review = reviewed.status === "done" && shapes.ready ? { places: reviewed.places, shapes: shapes.data } : null;
    const reviewFailed = reviewed.status === "error" || (!!shapes.error && !shapes.ready);
    if (on && drilled) {
      if (reviewFailed) return FAILED;
      if (!review) return READING;
      return { ...DONE, rings: ringsOfCounts(countsOfShapes(review.places, review.shapes), review), reviewed: true, reviewFailed: false, groupsRead: "done", firstMs: reviewed.firstMs, lastMs: reviewed.lastMs };
    }
    // While the next search's counts are read the page stands on the last
    // ones (lib/dataClient.js keeps them in `data` meanwhile).
    if (!on || read.loading || (!read.data && !read.error)) return READING;
    if (!read.data) return FAILED;
    return {
      ...DONE,
      rings: ringsOfCounts(read.data, review),
      reviewed: !!review,
      reviewFailed,
      groupsRead: read.data.groupsFailed ? "failed" : "done",
      firstMs: read.data.firstMs,
      lastMs: read.data.lastMs,
    };
  }, [on, drilled, read.data, read.loading, read.error, reviewed, shapes.ready, shapes.data, shapes.error]);
}
